import { IFilterTypes, IPcaPosition, IRangeFilters } from 'src/@operations/props';
import {
  OperationSummaryFilter,
  OperatorCategory,
  OperationType,
  AircraftCategory,
} from 'src/app/gql/__generated__/hooks';
import { convertSecondsToTimeString, convertTimeStringToSeconds } from 'src/utils';

const infringementKeys = [
  {
    key: 'hasCcoInfringements',
    name: 'CCO',
  },
  {
    key: 'hasCdoInfringements',
    name: 'CDO',
  },
  {
    key: 'hasComplaints',
    name: 'Complaints',
  },
  {
    key: 'hasCorrelations',
    name: 'Correlations',
  },
  {
    key: 'hasCorridorInfringements',
    name: 'Corridor',
  },
  {
    key: 'hasCurfewInfringements',
    name: 'Curfew',
  },
  {
    key: 'hasExclusionInfringements',
    name: 'Exclusion',
  },
  {
    key: 'hasGateInfringements',
    name: 'Gate',
  },
  {
    key: 'hasMinHeightInfringements',
    name: 'MinHeight',
  },
  {
    key: 'hasNoiseEvents',
    name: 'NoiseEvents',
  },
  {
    key: 'hasNoiseInfringements',
    name: 'Noise',
  },
];

export const tableFiltersToOperationSummaryFilter = (
  tableFilters: IFilterTypes,
  time: IRangeFilters,
  position?: IPcaPosition,
  intersectionFilter?: { gateIds: number[] }
): {
  filter: Partial<OperationSummaryFilter>;
  position?: IPcaPosition;
  intersectionFilter?: { gateIds: number[] };
} => {
  const result: Partial<OperationSummaryFilter> = {};

  if (tableFilters.acid) {
    result.acids = [tableFilters.acid];
  }

  if (tableFilters.tailNumber) {
    result.tailNumbers = [tableFilters.tailNumber];
  }

  if (tableFilters.operatorCategories.length > 0) {
    result.operatorCategories = tableFilters.operatorCategories.map(
      item => item.key as OperatorCategory
    );
  }

  if (tableFilters.operationTypes.length > 0) {
    result.operationTypes = tableFilters.operationTypes.map(item => item.key as OperationType);
  }

  if (tableFilters.runwayNames.length > 0) {
    result.runwayNames = tableFilters.runwayNames.map(item => item.key);
  }

  if (tableFilters.airportIds.length > 0) {
    result.airportIds = tableFilters.airportIds.map(item => item.key);
  }

  if (tableFilters.remoteAirportIds.length > 0) {
    result.remoteAirportIds = tableFilters.remoteAirportIds.map(item => item.key);
  }

  if (tableFilters.aircraftTypes.length > 0) {
    result.aircraftTypes = tableFilters.aircraftTypes.map(item => item.key);
  }

  if (tableFilters.aircraftCategories.length > 0) {
    result.aircraftCategories = tableFilters.aircraftCategories.map(
      item => item.key as AircraftCategory
    );
  }

  if (tableFilters.airlines.length > 0) {
    result.airlines = tableFilters.airlines.map(item => item.key);
  }

  tableFilters.correlated.forEach(item => {
    const infringementKey = infringementKeys.find(inf => inf.name === item.key);
    if (infringementKey) {
      (result as any)[infringementKey.key] = true;
    } else if (item.key === 'Infringements') {
      result.hasInfringements = true;
    } else if (item.key === 'NullValue') {
      result.hasInfringements = false;
    }
  });

  if (time.from && time.to) {
    result.timePeriod = {
      start: convertTimeStringToSeconds(time.from),
      end: convertTimeStringToSeconds(time.to),
    };
  }

  return {
    filter: result,
    position,
    intersectionFilter,
  };
};

export const urlToOpFilterState = (
  input: OperationSummaryFilter
): {
  time: IRangeFilters;
  tableFilters: IFilterTypes;
  position?: IPcaPosition;
} => {
  const tableFilters: IFilterTypes = {
    operationTypes: [],
    operatorCategories: [],
    operationTags: [],
    runwayNames: [],
    airportIds: [],
    remoteAirportIds: [],
    aircraftTypes: [],
    aircraftCategories: [],
    airlines: [],
    correlated: [],
    acid: '',
    tailNumber: '',
    gateIds: [],
  };
  const time = { from: '', to: '' };
  let position: IPcaPosition | undefined;

  Object.entries(input).forEach(([key, value]) => {
    const tempValue = value as string[];

    if (key === 'acids') {
      tableFilters.acid = Array.isArray(value) && value.length > 0 ? (value[0] as string) : '';
    } else if (key === 'tailNumbers') {
      tableFilters.tailNumber =
        Array.isArray(value) && value.length > 0 ? (value[0] as string) : '';
    } else if (key === 'operatorCategories') {
      tableFilters.operatorCategories = tempValue.map((category: string) => ({
        key: category,
        label: category,
        icon: `ac-${category.toLowerCase()}`,
        className: 'filter-icon-ac',
      }));
    } else if (key === 'operationTypes') {
      tableFilters.operationTypes = tempValue.map((category: string) => ({
        key: category,
        label: category,
        icon: `ad-${category.toLowerCase()}`,
        className: 'filter-icon-operation',
      }));
    } else if (infringementKeys.some(item => item.key === key)) {
      // Find the matching infringement key and get its name
      const matchingKey = infringementKeys.find(item => item.key === key);
      const type = matchingKey ? matchingKey.name : key;

      if (value) {
        tableFilters.correlated.push({
          key: type,
          label: type,
          icon: `co-${type.toLowerCase()}`,
          className: 'filter-icon-co',
        });
      }
    } else if (key === 'hasInfringements') {
      if (!value) {
        tableFilters.correlated.push({
          key: 'NullValue',
          label: 'Empty',
          icon: 'co-nullvalue',
          className: 'filter-icon-co',
        });
      } else if (value) {
        tableFilters.correlated.push({
          key: 'Infringements',
          label: 'Infringements',
          icon: 'co-infringements',
          className: 'filter-icon-co',
        });
      }
    } else if (key === 'timePeriod') {
      const arr = value as Record<string, number>;
      if (arr && typeof arr.start === 'number' && typeof arr.end === 'number') {
        time.from = convertSecondsToTimeString(arr.start);
        time.to = convertSecondsToTimeString(arr.end);
      }
    } else if (key === 'position') {
      const pos = (value as unknown) as IPcaPosition;
      position = pos;
    } else if (key === 'gateIds') {
      tableFilters.gateIds = Array.isArray(value) && value.length > 0 ? [value[0] as number] : [];
    } else {
      const items = value as string[] | number[];
      tableFilters[key] = items.map((category: string | number) => ({
        key: category,
        label: category,
        icon: undefined,
        className: '',
      }));
    }
  });

  return {
    time,
    tableFilters,
    position,
  };
};
